import { useMutation, useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import {
    getOrganizationPaymentIntegrationStatusCacheKey,
    longTermCacheStaleTime,
    onSetupTestConnectedAccount,
} from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { useIsOrganizationBillingAdmin, useOrganizationId } from './useOrganization';
import { getClientType } from '../useClientType';
import { useIsE2E } from '../useIsE2E';

const intl = getIntl('useOrganizationPaymentIntegration', {
    getSetupLinkFailure: 'Failed to get setup link',
    getStatusFailure: 'Failed to get payment integration status',
    getOnboardingUrlFailure: 'Failed to initiate Stripe onboarding',
    getDashboardUrlFailure: 'Failed to connect to Stripe',
    successfulTestOrganizationSetup: 'Successfully setup test organization',
    failedTestOrganizationSetup: 'Failed to setup test organization',
});

export type UseOrganizationPaymentIntegrationArgs = {
    onRetrieveSetupLinkSuccess?: (setupLink: string) => void;
    onRetrieveOnboardingUrlSuccess?: (onboardingUrl: string) => void;
    onRetrieveDashboardUrlSuccess?: (dashboardUrl: string) => void;
    onSetupTestOrganizationSuccess?: () => void;
};

export type UpdateArgs = {
    groupId: string;
    group: APITypes.organizationService.UpdateOrganizationGroupRequest;
};

export type UseOrganizationPaymentIntegrationReturnValue = {
    paymentIntegrationStatus: APITypes.organizationService.GetOrganizationPaymentIntegrationStatusResponse | null;
    isLoadingPaymentIntegrationStatus: boolean;
    retrieveSetupLink: () => void;
    isRetrievingSetupLink: boolean;
    retrieveOnboardingUrl: () => void;
    isRetrievingOnboardingUrl: boolean;
    retrieveDashboardUrl: () => void;
    isRetrievingDashboardUrl: boolean;
    isOrganizationInValidStripeState: boolean;
    setupTestOrganization: () => void;
};

export default function useOrganizationPaymentIntegration({
    onRetrieveDashboardUrlSuccess,
    onRetrieveOnboardingUrlSuccess,
    onRetrieveSetupLinkSuccess,
    onSetupTestOrganizationSuccess,
}: UseOrganizationPaymentIntegrationArgs): UseOrganizationPaymentIntegrationReturnValue {
    const isE2E = useIsE2E();
    const isBillingAdmin = useIsOrganizationBillingAdmin();
    const clientType = getClientType();
    const getIsWebClientType = (
        clientType: APITypes.sharedTypes.ClientType,
    ): clientType is APITypes.paymentService.WebStripeClientType => {
        return ['local-web', 'dev-web', 'prod-web'].includes(clientType);
    };

    const organizationId = useOrganizationId();
    const notify = useNotify();
    const paymentIntegrationStatusQuery = useQuery({
        queryKey: getOrganizationPaymentIntegrationStatusCacheKey(),
        queryFn: async () => {
            return await appAPI.get<APITypes.organizationService.GetOrganizationPaymentIntegrationStatusResponse>(
                urls.api.organizations.paymentIntegration.status(organizationId),
            );
        },
        enabled: isBillingAdmin,
        staleTime: longTermCacheStaleTime,
    });
    useQueryErrorNotify(paymentIntegrationStatusQuery, intl('getStatusFailure'));

    const retrieveSetupLinkMutation = useMutation({
        mutationFn: async () => {
            if (!getIsWebClientType(clientType)) {
                throw new Error('Unsupported client type');
            }

            const request: APITypes.organizationService.SetupOrganizationPaymentIntegrationRequest = {
                client: clientType,
            };

            const result = await appAPI.post<APITypes.organizationService.SetupOrganizationPaymentIntegrationResponse>(
                urls.api.organizations.paymentIntegration.getSetupUrl(organizationId),
                { body: request },
            );
            return result;
        },
        onSuccess: (data) => {
            onRetrieveSetupLinkSuccess?.(data.onboardingUrl);
        },
        onError: () => {
            notify('error', intl('getSetupLinkFailure'));
        },
    });

    const retrieveOnboardingUrlMutation = useMutation({
        mutationFn: async () => {
            if (!getIsWebClientType(clientType)) {
                throw new Error('Unsupported client type');
            }

            const request: APITypes.organizationService.GetOrganizationPaymentIntegrationOnboardingUrlRequest = {
                client: clientType,
            };

            const result =
                await appAPI.post<APITypes.organizationService.GetOrganizationPaymentIntegrationOnboardingUrlResponse>(
                    urls.api.organizations.paymentIntegration.getOnboardingUrl(organizationId),
                    { body: request },
                );
            return result;
        },
        onSuccess: (data) => {
            onRetrieveOnboardingUrlSuccess?.(data.onboardingUrl);
        },
        onError: () => {
            notify('error', intl('getOnboardingUrlFailure'));
        },
    });

    const retrieveDashboardUrlMutation = useMutation({
        mutationFn: async () => {
            const result =
                await appAPI.get<APITypes.organizationService.GetOrganizationPaymentIntegrationDashboardUrlResponse>(
                    urls.api.organizations.paymentIntegration.getDashboardUrl(organizationId),
                );
            return result;
        },
        onSuccess: (data) => {
            onRetrieveDashboardUrlSuccess?.(data.dashboardUrl);
        },
        onError: () => {
            notify('error', intl('getDashboardUrlFailure'));
        },
    });

    const setupTestOrganizationMutation = useMutation({
        mutationFn: async () => {
            if (!isE2E) {
                throw new Error('Only available in E2E');
            }

            const result = await appAPI.post(
                urls.api.organizations.paymentIntegration.setupTestOrganization(organizationId),
            );
            return result;
        },
        onSuccess: () => {
            onSetupTestConnectedAccount.resetCache({})();
            notify('success', intl('successfulTestOrganizationSetup'));
            onSetupTestOrganizationSuccess?.();
        },
        onError: () => {
            notify('error', intl('failedTestOrganizationSetup'));
        },
    });

    return {
        paymentIntegrationStatus: paymentIntegrationStatusQuery.data || null,
        isLoadingPaymentIntegrationStatus: paymentIntegrationStatusQuery.isLoading,
        retrieveSetupLink: retrieveSetupLinkMutation.mutate,
        isRetrievingSetupLink: retrieveSetupLinkMutation.isPending,
        retrieveOnboardingUrl: retrieveOnboardingUrlMutation.mutate,
        isRetrievingOnboardingUrl: retrieveOnboardingUrlMutation.isPending,
        retrieveDashboardUrl: retrieveDashboardUrlMutation.mutate,
        isRetrievingDashboardUrl: retrieveDashboardUrlMutation.isPending,
        isOrganizationInValidStripeState: paymentIntegrationStatusQuery.data?.status === 'active',
        setupTestOrganization: setupTestOrganizationMutation.mutate,
    };
}
